import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { MapPin, RefreshCw, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import CountrySelect, {
  DEFAULT_COUNTRY_CODE,
  getCountryFlag,
  getCountryLabel,
  countryMatchesFilter,
  normalizeCountryCode,
} from '../components/CountrySelect';
import { completeDialogSubmit } from '../utils/formUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createInitialFormData = (userCountry) => ({
  vehicle_id: '',
  latitude: '',
  longitude: '',
  address: '',
  city: '',
  country: normalizeCountryCode(userCountry) || DEFAULT_COUNTRY_CODE,
  speed_kmh: '',
  source: 'MANUAL',
  driver_id: '',
});

const VehicleMap = () => {
  const [locations, setLocations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');

  const [formData, setFormData] = useState(createInitialFormData());

  const fetchData = useCallback(async () => {
    try {
      const [locationsRes, vehiclesRes, driversRes] = await Promise.all([
        axios.get(`${API}/vehicle-locations`),
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/drivers`),
      ]);
      setLocations(locationsRes.data);
      setVehicles(vehiclesRes.data);
      setDrivers(driversRes.data);
    } catch {
      // Silent fail for background refresh
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const countriesWithLocations = useMemo(() => {
    const codes = new Set();
    locations.forEach((l) => {
      const code = normalizeCountryCode(l.country);
      if (code) codes.add(code);
    });
    return Array.from(codes).sort();
  }, [locations]);

  const onVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find((item) => item.id === vehicleId);
    setFormData({
      ...formData,
      vehicle_id: vehicleId,
      country: normalizeCountryCode(vehicle?.country) || formData.country || DEFAULT_COUNTRY_CODE,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.country) {
      return;
    }

    await completeDialogSubmit({
      submit: () =>
        axios.post(`${API}/vehicle-locations`, {
          ...formData,
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude),
          speed_kmh: formData.speed_kmh ? parseFloat(formData.speed_kmh) : null,
          country: normalizeCountryCode(formData.country),
          city: formData.city?.trim() || null,
          driver_id: formData.driver_id || null,
        }),
      setDialogOpen,
      setFormData,
      initialFormData: createInitialFormData,
      onSuccess: fetchData,
      successMessage: 'Vehicle location updated!',
      errorMessage: 'Failed to update location',
    });
  };

  const filteredLocations = selectedCountry
    ? locations.filter((l) => countryMatchesFilter(l.country, selectedCountry))
    : locations;

  if (loading) {
    return <div className="p-8 text-center">Loading fleet map...</div>;
  }

  return (
    <div className="p-6 lg:p-8" data-testid="vehicle-map-page">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Fleet Map</h1>
          <p className="text-slate-600 mt-1">Real-time vehicle locations across all countries</p>
        </div>
        <div className="flex gap-2 flex-wrap items-start">
          <CountrySelect
            value={selectedCountry || 'ALL'}
            onValueChange={(v) => setSelectedCountry(v === 'ALL' ? '' : v)}
            includeAllOption
            allLabel="All Countries"
            allowedCodes={countriesWithLocations}
            className="w-56"
          />
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="update-location-btn">
                <MapPin size={18} className="mr-2" />
                Update Location
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Update Vehicle Location</DialogTitle>
                <DialogDescription>Manually update a vehicle&apos;s location (GPS backup).</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Vehicle</Label>
                  <Select value={formData.vehicle_id} onValueChange={onVehicleChange}>
                    <SelectTrigger><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                    <SelectContent>
                      {vehicles.map(veh => (
                        <SelectItem key={veh.id} value={veh.id}>{veh.registration_number} - {getCountryLabel(veh.country) || veh.country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Country *</Label>
                  <CountrySelect
                    value={formData.country}
                    onValueChange={(v) => setFormData({ ...formData, country: v })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Latitude</Label>
                    <Input type="number" step="any" value={formData.latitude} onChange={(e) => setFormData({...formData, latitude: e.target.value})} placeholder="5.6037" required />
                  </div>
                  <div>
                    <Label>Longitude</Label>
                    <Input type="number" step="any" value={formData.longitude} onChange={(e) => setFormData({...formData, longitude: e.target.value})} placeholder="-0.1870" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Address</Label>
                    <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Street address" />
                  </div>
                  <div>
                    <Label>City (optional)</Label>
                    <Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} placeholder="City name" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Speed (km/h)</Label>
                    <Input type="number" value={formData.speed_kmh} onChange={(e) => setFormData({...formData, speed_kmh: e.target.value})} placeholder="0" />
                  </div>
                  <div>
                    <Label>Driver</Label>
                    <Select value={formData.driver_id || 'NONE'} onValueChange={(value) => setFormData({...formData, driver_id: value === 'NONE' ? '' : value})}>
                      <SelectTrigger><SelectValue placeholder="Select driver" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">None</SelectItem>
                        {drivers.map(d => (
                          <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">Update Location</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Country Summary Cards — only countries that currently have locations */}
      {countriesWithLocations.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {countriesWithLocations.map((code) => {
            const countryLocations = locations.filter((l) => countryMatchesFilter(l.country, code));
            return (
              <div
                key={code}
                className={`fleet-card cursor-pointer hover:shadow-lg transition-shadow ${selectedCountry && countryMatchesFilter(selectedCountry, code) ? 'ring-2 ring-amber-500' : ''}`}
                onClick={() => setSelectedCountry(selectedCountry && countryMatchesFilter(selectedCountry, code) ? '' : code)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{getCountryLabel(code) || code}</h3>
                    <p className="text-slate-500 text-sm">{countryLocations.length} vehicles tracked</p>
                  </div>
                  <div className="text-4xl">{getCountryFlag(code) || '📍'}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Map Placeholder */}
      <div className="fleet-card mb-6">
        <div className="bg-slate-100 rounded-lg h-96 flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-green-100 opacity-50"></div>
          <div className="relative z-10 text-center">
            <MapPin size={48} className="text-amber-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-800">Fleet Map View</h3>
            <p className="text-slate-600 mt-2">
              {filteredLocations.length} vehicles with location data
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Integrate with Google Maps API for full map functionality
            </p>
          </div>

          <div className="absolute bottom-4 left-4 right-4 flex gap-2 flex-wrap">
            {filteredLocations.slice(0, 10).map((loc) => (
              <div key={loc.vehicle_id} className="bg-white rounded-full px-2 py-1 text-xs shadow flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${loc.status === 'ACTIVE' ? 'bg-green-500' : 'bg-slate-400'}`}></div>
                {loc.registration_number}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Vehicle Locations Table */}
      <div className="fleet-card">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Vehicle Locations</h3>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>Location</th>
                <th>Coordinates</th>
                <th>Speed</th>
                <th>Last Updated</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredLocations.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-slate-500">
                    No location data available. Update vehicle locations manually or connect GPS devices.
                  </td>
                </tr>
              ) : (
                filteredLocations.map(loc => (
                  <tr key={loc.vehicle_id}>
                    <td>
                      <div className="font-semibold">{loc.registration_number}</div>
                      <div className="text-xs text-slate-500">{loc.make} {loc.model}</div>
                    </td>
                    <td>
                      <div>{loc.address || '-'}</div>
                      <div className="text-xs text-slate-500">
                        {[loc.city, getCountryLabel(loc.country) || loc.country].filter(Boolean).join(', ')}
                      </div>
                    </td>
                    <td className="font-mono text-sm">
                      {loc.latitude?.toFixed(4)}, {loc.longitude?.toFixed(4)}
                    </td>
                    <td>
                      {loc.speed_kmh ? (
                        <span className={`font-semibold ${loc.speed_kmh > 100 ? 'text-red-600' : 'text-slate-800'}`}>
                          {loc.speed_kmh} km/h
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Clock size={14} />
                        {loc.timestamp ? new Date(loc.timestamp).toLocaleString() : '-'}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${loc.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}`}>
                        {loc.status || 'Unknown'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VehicleMap;
